import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CategoryService } from 'app/services/category/category.service';
import { ImageUrlPipe } from 'app/modules/admin/pipe/image-url.pipe';
import { GalleryPickerComponent } from 'app/shared/gallery-picker/gallery-picker.component';
import { ImageFallbackDirective } from 'app/modules/admin/directives/ImageFallbackDirective';
import { ImageItem } from 'app/services/gallery/gallery.service';

@Component({
  selector: 'app-add-category',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ImageUrlPipe,
    GalleryPickerComponent,
    ImageFallbackDirective
  ],
  templateUrl: './add-category.component.html',
  styleUrls: ['./add-category.component.scss']
})
export class AddCategoryComponent implements OnInit {
  categoryForm: FormGroup;

  // قائمة الفئات الأب القادمة من السيرفر (للصفحة الحالية فقط)
  parentCategories: any[] = [];
  
  // متغيرات التصفّح (مطابقة لـ CategoriesComponent)
  currentPage: number = 1;
  itemsPerPage: number = 10;  // يُستخدم فقط في العرض وليس في الطلب حالياً
  totalItems: number = 0;
  totalPages: number = 0;
  
  // البحث عن الفئة الأب
  searchTermParent: string = '';

  isLoading = false;
  isSubmitting = false;

  isGalleryModalOpen = false;
  selectedPhotos: ImageItem[] = [];
  selectedIconId: number | null = null;
  imagePreview: string | null = null;

  constructor(
    private fb: FormBuilder,
    private categoryService: CategoryService,
    private router: Router
  ) {
    this.categoryForm = this.createForm();
  }

  ngOnInit() {
    this.loadParentCategories(1);
  }

  createForm(): FormGroup {
    return this.fb.group({
      parentId: [0, [Validators.required, Validators.min(0)]],
      level: [0, [Validators.required, Validators.min(0)]],
      code: ['', [Validators.required, Validators.minLength(1)]],
      arName: ['', [Validators.required, Validators.minLength(2)]],
      enName: ['', [Validators.required, Validators.minLength(2)]],
      icon: [null],
      featured: [false],
      top: [false],
      slug: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9]+$/)
        ]
      ],
      status: [true]
    });
  }

  // 🔥 تحميل الفئات الأب من السيرفر مباشرة (نفس نقطة النهاية المستخدمة في CategoriesComponent)
  loadParentCategories(page: number = 1) {
    if (this.isLoading) return;

    this.isLoading = true;
    this.categoryService.getSubCategories(this.searchTermParent, page)
      .subscribe({
        next: (response) => {
          this.parentCategories = response.data || [];
          this.currentPage = response.meta?.currentPage || page;
          this.totalItems = response.meta?.total || 0;
          this.totalPages = response.meta?.to || 0;
          this.isLoading = false;
        },
        error: (err) => {
          console.error('❌ Error loading parent categories:', err);
          this.isLoading = false;
          this.parentCategories = [];
          this.totalItems = 0;
          this.totalPages = 0;
        }
      });
  }

  // دوال البحث والتصفّح (مطابقة لـ CategoriesComponent)
  onParentSearch(event: any) {
    this.searchTermParent = event.target.value.trim();
    this.currentPage = 1;
    this.loadParentCategories(1);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.loadParentCategories(page);
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }

  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (this.totalPages <= 1) return [1];

    let startPage = Math.max(1, this.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  onItemsPerPageChange(event: any) {
    const value = parseInt(event.target.value, 10);
    if (value > 0) {
      this.itemsPerPage = value;
      this.currentPage = 1;
      this.loadParentCategories(1);
    }
  }

  getStartIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  getEndIndex(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
  }

  // معرض الصور (كما هو)
  openGalleryModal(): void {
    this.isGalleryModalOpen = true;
  }

  closeGalleryModal(): void {
    this.isGalleryModalOpen = false;
  }

  onGalleryImagesChange(images: ImageItem[]): void {
    this.selectedPhotos = images || [];
    const firstImage = this.selectedPhotos[0];
    this.selectedIconId = firstImage ? Number(firstImage.id) : null;
    this.imagePreview = firstImage?.url || null;
    this.categoryForm.patchValue({ icon: this.selectedIconId });
    this.closeGalleryModal();
  }

  removeImage(): void {
    this.selectedPhotos = [];
    this.selectedIconId = null;
    this.imagePreview = null;
    this.categoryForm.patchValue({ icon: null });
  }

  // إرسال النموذج (كما هو)
  async onSubmit() {
    if (this.categoryForm.invalid) {
      this.markAllFieldsAsTouched();
      return;
    }

    this.isSubmitting = true;
    try {
      const formData = {
        ...this.categoryForm.value,
        icon: this.selectedIconId
      };
      this.categoryService.createCategory(formData).subscribe({
        next: () => {
          this.isSubmitting = false;
          alert('✅ Category added successfully!');
          this.router.navigateByUrl('/admin/categories');
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('❌ Error adding category:', error);
          alert('⚠️' + (error?.error?.message || 'Error adding category'));
        }
      });
    } catch (error) {
      this.isSubmitting = false;
      console.error('❌ Error preparing form data:', error);
      alert('⚠️ Error preparing form data.');
    }
  }

  markAllFieldsAsTouched() {
    Object.keys(this.categoryForm.controls).forEach(key => {
      this.categoryForm.get(key)?.markAsTouched();
    });
  }

  showError(controlName: string): boolean {
    const control = this.categoryForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getError(controlName: string): string {
    const control = this.categoryForm.get(controlName);
    if (control?.errors) {
      if (control.errors['required']) return 'This field is required';
      if (control.errors['minlength']) return `Minimum length is ${control.errors['minlength'].requiredLength}`;
      if (control.errors['min']) return `Minimum value is ${control.errors['min'].min}`;
      if (control.errors['max']) return `Maximum value is ${control.errors['max'].max}`;
    }
    return 'Invalid value';
  }

  getCategoryName(category: any): string {
    return category?.name || category?.enName || 'Unnamed Category';
  }

  goBack() {
    this.router.navigateByUrl('/admin/categories');
  }
}